<script setup lang="ts">
import { computed } from 'vue';
import { InputText, InputNumber, MultiSelect, Select } from 'primevue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import type { ColumnFilterOption, ColumnFilterType } from 'shared/common/composables/useColumnSelector';

const props = defineProps<{
  filterType?: ColumnFilterType;
  filterModel: { value: unknown };
  filterCallback: () => void;
  placeholder?: string;
  options?: ColumnFilterOption[];
}>();

const { translations } = storeToRefs(useLanguageStore());

const textValue = computed<string | null>({
  get: () => (props.filterModel.value as string | null) ?? null,
  set: (value) => { props.filterModel.value = value; },
});

const numberValue = computed<number | null>({
  get: () => (props.filterModel.value as number | null) ?? null,
  set: (value) => { props.filterModel.value = value; },
});

const multiValue = computed<unknown[] | null>({
  get: () => (props.filterModel.value as unknown[] | null) ?? null,
  set: (value) => { props.filterModel.value = value; },
});

const boolValue = computed<unknown>({
  get: () => props.filterModel.value,
  set: (value) => { props.filterModel.value = value; },
});

const booleanOptions = computed<ColumnFilterOption[]>(() => [
  { label: translations.value.generic.yes, value: true },
  { label: translations.value.generic.no, value: false },
]);
</script>

<template>
  <InputText v-if="filterType === 'text'" v-model="textValue" size="small" type="text"
    @input="filterCallback()" :placeholder="placeholder" fluid />

  <InputNumber v-else-if="filterType === 'numeric'" v-model="numberValue" mode="decimal"
    @input="filterCallback()" fluid />

  <MultiSelect v-else-if="filterType === 'select'" v-model="multiValue" @change="filterCallback()"
    :options="options" optionLabel="label" optionValue="value" :placeholder="placeholder" size="small"
    style="min-width: 12rem" appendTo="self" />

  <Select v-else-if="filterType === 'boolean'" v-model="boolValue" @change="filterCallback()"
    :options="booleanOptions" optionLabel="label" optionValue="value" :placeholder="placeholder"
    size="small" showClear fluid appendTo="self" />
</template>
